// src/components/transfers/CreateDialog.jsx
import React, { useState, useEffect } from 'react';
import { X, Package } from 'lucide-react';
import ProductPicker from './ProductPicker';
import { storeApi, productApi } from '../../api';
import { useApp } from '../../context/AppContext';

// ⚠️ 重要改动：这个弹窗之前完全没有"选择要向哪个门店申请"这一步，商品列表
// 直接用父组件传下来的固定数据——那是"分店只能向总店申请"这个旧假设下的写法。
// 现在改成先选门店，选完之后再动态去查那个门店的库存（不含成本价），
// 选门店之前，商品列表是空的，不允许瞎选
const CreateDialog = ({ onClose, onCreate }) => {
  const { currentUser } = useApp();
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [fromStoreId, setFromStoreId] = useState('');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    setLoadingStores(true);
    try {
      const res = await storeApi.getList();
      if (res.success) {
        // 排除自己所在的门店——不能向自己申请调货
        setStores((res.data || []).filter(s => s.status === 'active' && s.id !== currentUser?.store_id));
      }
    } catch (err) {
      console.error('加载门店列表失败:', err);
    } finally {
      setLoadingStores(false);
    }
  };

  const handleSelectFromStore = async (storeId) => {
    setFromStoreId(storeId);
    setItems([]); // 换了门店，之前选的商品清空（不同门店的商品ID不能混用）
    if (!storeId) { setProducts([]); return; }
    setLoadingProducts(true);
    try {
      const res = await productApi.getProductsByStore(storeId);
      if (res.success) setProducts(res.data || []);
      else setProducts([]);
    } catch (err) {
      console.error('加载门店库存失败:', err);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleAddItem = (item) => setItems(prev => [...prev, item]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const updateBoxCount = (idx, newCount) => {
    setItems(prev => {
      const list = [...prev];
      const item = list[idx];
      const count = Math.max(1, parseInt(newCount) || 1);
      list[idx] = { ...item, box_count: count, quantity: item.box_quantity ? count * item.box_quantity : item.quantity };
      return list;
    });
  };

  const updateUnitCount = (idx, newCount) => {
    setItems(prev => {
      const list = [...prev];
      list[idx] = { ...list[idx], quantity: Math.max(1, parseInt(newCount) || 1) };
      return list;
    });
  };

  const handleSubmit = async () => {
    if (!fromStoreId) { setError('请先选择要向哪个门店申请调货'); return; }
    if (items.length === 0) { setError('请至少添加一个商品'); return; }
    setCreating(true); setError('');
    try {
      await onCreate({
        from_store_id: parseInt(fromStoreId),
        notes,
        items: items.map(i => ({ product_id: parseInt(i.product_id), quantity: parseInt(i.quantity), transfer_unit: i.transfer_unit || 'unit' }))
      });
    } catch (err) {
      setError(err.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-lg font-bold">发起调货申请</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
            📦 有箱装的商品按整箱申请；无箱装商品输入个数
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">向哪个门店申请调货</label>
            <select
              value={fromStoreId}
              onChange={e => handleSelectFromStore(e.target.value)}
              disabled={loadingStores}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value="">{loadingStores ? '加载中...' : '请选择门店'}</option>
              {stores.map(s => (
                <option key={s.id} value={s.id}>{s.name}{s.type === 'headquarters' ? '（总店）' : ''}</option>
              ))}
            </select>
          </div>

          {fromStoreId && (
            <div>
              <label className="block text-sm font-medium mb-2">选择商品</label>
              {loadingProducts ? (
                <p className="text-sm text-gray-400 py-4 text-center">加载该门店库存中...</p>
              ) : products.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">该门店暂无可调货的商品</p>
              ) : (
                <ProductPicker products={products} selectedItems={items} onAdd={handleAddItem} />
              )}
            </div>
          )}

          {items.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2">已选商品（{items.length} 种）</label>
              <div className="space-y-2">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl border">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                      {item.product_image
                        ? <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center"><Package className="w-5 h-5 text-gray-400" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.product_name}</p>
                      {item.transfer_unit === 'box' ? (
                        <div className="flex items-center gap-2 mt-1">
                          <button onClick={() => updateBoxCount(idx, (item.box_count || 1) - 1)} className="w-6 h-6 rounded bg-white border text-xs flex items-center justify-center hover:bg-gray-100">−</button>
                          <span className="text-sm font-bold text-orange-600">{item.box_count} 箱</span>
                          <button onClick={() => updateBoxCount(idx, (item.box_count || 1) + 1)} className="w-6 h-6 rounded bg-white border text-xs flex items-center justify-center hover:bg-gray-100">＋</button>
                          <span className="text-xs text-gray-400">= {item.quantity} 个</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <button onClick={() => updateUnitCount(idx, (item.quantity || 1) - 1)} className="w-6 h-6 rounded bg-white border text-xs flex items-center justify-center hover:bg-gray-100">−</button>
                          <span className="text-sm font-bold text-gray-700">{item.quantity} 个</span>
                          <button onClick={() => updateUnitCount(idx, (item.quantity || 1) + 1)} className="w-6 h-6 rounded bg-white border text-xs flex items-center justify-center hover:bg-gray-100">＋</button>
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 flex-shrink-0"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">备注</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} placeholder="备注信息（可选）" />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>}
        </div>
        <div className="p-6 border-t flex space-x-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">取消</button>
          <button onClick={handleSubmit} disabled={creating || items.length === 0}
            className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg ${creating || items.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}>
            {creating ? '提交中...' : `提交申请（${items.length} 种商品）`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateDialog;